import { CSSProperties, useEffect, useRef, useState } from 'react';
import {
  ADSTERRA_FEED_BANNER,
  AdsterraBannerConfiguration,
  isAdsterraBannerConfigurationValid,
} from '../../utils/adsterra-ads';
import './style.css';

declare global {
  interface Window {
    atOptions?: Record<string, unknown>;
  }
}

interface Props {
  className?: string;
  configuration?: AdsterraBannerConfiguration;
  onLoaded?: () => void;
  onError?: () => void;
}

type AdState = 'idle' | 'loading' | 'loaded' | 'error';

// O código Banner do Adsterra lê `window.atOptions`. A fila evita que dois
// slots virtuais sobrescrevam essas opções antes que o script anterior rode.
let scriptQueue: Promise<void> = Promise.resolve();

const loadBannerScript = (
  container: HTMLDivElement,
  configuration: AdsterraBannerConfiguration,
) => {
  const load = () =>
    new Promise<void>((resolve, reject) => {
      let settled = false;
      let loadTimer = 0;
      const settle = (error?: Error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(loadTimer);
        if (error) {
          reject(error);
          return;
        }
        resolve();
      };

      window.atOptions = {
        key: configuration.key,
        format: 'iframe',
        height: configuration.height,
        width: configuration.width,
        params: {},
      };

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = configuration.scriptUrl;
      script.async = false;
      script.dataset.adsterraScript = 'true';
      script.onload = () => settle();
      script.onerror = () =>
        settle(new Error('Não foi possível carregar o banner do Adsterra.'));
      loadTimer = window.setTimeout(
        () => settle(new Error('Tempo esgotado ao carregar o banner do Adsterra.')),
        10_000,
      );

      container.replaceChildren(script);
    });

  const currentLoad = scriptQueue.then(load, load);
  scriptQueue = currentLoad.catch(() => undefined);

  return currentLoad;
};

export default function AdsterraAdSlot({
  className = '',
  configuration = ADSTERRA_FEED_BANNER,
  onLoaded,
  onError,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onLoadedRef = useRef(onLoaded);
  const onErrorRef = useRef(onError);
  const [visible, setVisible] = useState(false);
  const [adState, setAdState] = useState<AdState>('idle');

  useEffect(() => {
    onLoadedRef.current = onLoaded;
    onErrorRef.current = onError;
  }, [onError, onLoaded]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || visible) return;

    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    const container = containerRef.current;
    if (
      !visible ||
      !container ||
      !isAdsterraBannerConfigurationValid(configuration) ||
      container.dataset.adRequested === 'true'
    ) {
      return;
    }

    container.dataset.adRequested = 'true';
    setAdState('loading');
    let active = true;

    loadBannerScript(container, configuration)
      .then(() => {
        if (!active) return;
        setAdState('loaded');
        onLoadedRef.current?.();
      })
      .catch(error => {
        if (!active) return;
        console.warn('[Adsterra] Não foi possível solicitar o anúncio.', error);
        setAdState('error');
        onErrorRef.current?.();
      });

    return () => {
      active = false;
    };
  }, [configuration, visible]);

  if (!isAdsterraBannerConfigurationValid(configuration)) return null;

  return (
    <div
      className={`adsterra-ad adsterra-ad--${adState} ${className}`.trim()}
      aria-label={adState === 'error' ? undefined : 'Publicidade'}
      aria-busy={adState === 'idle' || adState === 'loading'}
      aria-hidden={adState === 'error' ? true : undefined}
      style={{
        '--adsterra-banner-height': `${configuration.height}px`,
      } as CSSProperties}
    >
      {adState !== 'error' && (
        <span className="adsterra-ad__label">Publicidade</span>
      )}
      {adState === 'loading' && (
        <div className="adsterra-ad__loading" aria-hidden="true">
          <span className="adsterra-ad__loading-line" />
          <span className="adsterra-ad__loading-line adsterra-ad__loading-line--short" />
        </div>
      )}
      <div ref={containerRef} className="adsterra-ad__unit" />
    </div>
  );
}
